import { calendarAlignment } from "./text-styles";
import { type Page, type DesignNode } from "./schema";
import { type ScenePage, type SceneNode } from "./scene";
import { type FontContext } from "./fonts";
import { type Diagnostic } from "./engine";
import {
  calendarRuns,
  calendarHeight,
  calendarFitScale,
  layoutText,
  fitText,
  textFrameHeight,
  sliceRuns,
  type Run,
} from "./typography";
import { type Rect } from "./production";
interface Region extends Rect {
  id: string;
  gap: number;
  next?: string | null;
}
interface Position {
  page: number;
  region: string;
}
export interface FlowPlacement {
  regionId: string;
  start: number;
  end: number;
  total: number;
}
/** Packs visible blocks through region chains. Coordinates remain relative to trim. */
export function paginateFlow(
  source: Page,
  fixed: SceneNode[],
  flow: SceneNode[],
  options: {
    top: number;
    bottom: number;
    pageBudget: number;
    diagnostics: Diagnostic[];
    overflowMessage: string;
    typography: (node: DesignNode) => FontContext | undefined;
    fit?: (
      node: SceneNode,
      runs: Run[],
      width: number,
      height: number,
    ) => ReturnType<typeof fitText>;
  },
): ScenePage[] {
  const width = source.width || 612,
    height = source.height || 792;
  const bodyBottom = (source.height || 14400) - options.bottom;
  const regions = new Map<string, Region>([
    [
      "body",
      {
        id: "body",
        x: source.margin,
        y: options.top,
        width: width - source.margin * 2,
        height: bodyBottom - options.top,
        gap: 8,
        next: null,
      },
    ],
  ]);
  source.flowRegions.forEach((r, index) => {
    const y = Math.max(options.top, r.y),
      end = Math.min(bodyBottom, r.y + r.height);
    regions.set(r.id, {
      ...r,
      y,
      height: end - y,
      next:
        r.nextRegionId === undefined
          ? source.flowRegions[index + 1]?.id
          : r.nextRegionId,
    });
  });
  const pages: ScenePage[] = [];
  const pageAt = (index: number) => {
    if (index >= options.pageBudget)
      throw new Error("Document exceeds 100-page limit");
    while (pages.length <= index) {
      const i = pages.length;
      pages.push({
        id: i ? `${source.id}_continuation_${i}` : source.id,
        name: i ? `${source.name} · continued` : source.name,
        width,
        height,
        background: source.background,
        frame: source.frame,
        production: source.production,
        artBox: source.artBox,
        nodes: i ? [] : [...fixed],
        trim: { x: 0, y: 0, width, height },
      });
    }
    return pages[index];
  };
  pageAt(0);
  const cursors = new Map<string, number>(),
    threads = new Map<string, Position>();
  const key = (position: Position) => `${position.page}:${position.region}`;
  const regionAt = (position: Position): Region => {
    const region = regions.get(position.region);
    if (!region) throw new Error(`Unknown flow region: ${position.region}`);
    if (
      region.width <= 0 ||
      region.height <= 0 ||
      region.x + region.width > 14400
    )
      throw new Error(
        `Flow region ${region.id} has no usable area after margins and bands`,
      );
    return region;
  };
  const advance = (position: Position, start: string): Position => {
    const next = regionAt(position).next;
    return next
      ? { page: position.page, region: next }
      : { page: position.page + 1, region: start };
  };
  const warn = (n: SceneNode, pg: ScenePage) =>
    options.diagnostics.push({
      nodeId: n.sourceId,
      pageId: pg.id,
      code: n.type === "calendar" ? "CALENDAR_OVERFLOW" : "TEXT_OVERFLOW",
      message: `${n.name}: ${options.overflowMessage}`,
      severity: "warning",
    });
  let fragments = fixed.length;
  const sourceIds = new Set([...fixed, ...flow].map((n) => n.id)),
    usedIds = new Set(fixed.map((n) => n.id));
  const add = (position: Position, n: SceneNode) => {
    if (++fragments > 20000)
      throw new Error("Flow exceeds 20,000-layer budget");
    let id = n.id,
      index = 0;
    while (usedIds.has(id) || (sourceIds.has(id) && id !== n.sourceId))
      id = `${n.id}_flow_${++index}`;
    usedIds.add(id);
    pageAt(position.page).nodes.push({ ...n, id });
  };
  const ordered = flow
    .map((n, index) => ({ n, index }))
    .sort((a, b) => a.n.flowOrder - b.n.flowOrder || a.index - b.index);
  for (const { n: original } of ordered) {
    const start = original.flowRegionId || "body";
    let position = threads.get(start) || { page: 0, region: start },
      offset = 0;
    const allRuns: Run[] = original.richRuns || [{ text: original.text }];
    const total = allRuns.reduce((sum, run) => sum + run.text.length, 0);
    if (total > 1000000)
      throw new Error("Flow text exceeds one million characters per layer");
    const isText = ["text", "richtext"].includes(original.type);
    let moves = 0;
    while (true) {
      if (++moves > 3300)
        throw new Error("Flow could not fit content within the page budget");
      const region = regionAt(position),
        cursor = Math.max(region.y, cursors.get(key(position)) ?? region.y);
      const room = region.y + region.height - cursor;
      const n = {
        ...original,
        x: region.x,
        y: cursor,
        width:
          original.flowWidth === "node" || start === "body"
            ? Math.min(original.width, region.width)
            : region.width,
      };
      const remaining = sliceRuns(allRuns, offset);
      let text = isText
        ? layoutText(
            remaining,
            n.width,
            n.fontSize,
            n.lineHeight,
            n.fontFamily,
            n.fontWeight,
            n.align,
            options.typography(n),
            n.textHeightMode === "font-box",
          )
        : undefined;
      const measureCalendar = () =>
        calendarHeight(
          calendarRuns(
            n.text,
            n.width,
            n.fontFamily,
            n.fontWeight,
            options.typography(n),
            n.fontSize,
            n.lineHeight,
            calendarAlignment(n),
          ),
          n.fontFamily,
          n.fontWeight,
          options.typography(n),
        );
      const measured =
        n.type === "calendar"
          ? measureCalendar()
          : text
            ? textFrameHeight(text, false, n.textHeightMode === "font-box")
            : n.height;
      const requested = Math.max(
        n.minHeight,
        Math.min(
          n.maxHeight,
          n.flowGrow ? Math.max(n.height, measured) : n.height,
        ),
      );
      const continueText = isText && n.overflow === "continue";
      const limit = Math.min(
        room,
        n.maxHeight,
        n.flowGrow ? Infinity : requested,
      );
      if (continueText) {
        const fitting = text!.lines.filter(
          (line) => (line.fontBoxBottom ?? line.bottom) <= limit + 1e-7,
        );
        if (!fitting.length || room < n.minHeight) {
          // A line may fit in a later, taller region. Never allocate an empty continuation page.
          let probe = advance(position, start),
            attempts = 0;
          while (true) {
            if (probe.page >= options.pageBudget)
              throw new Error("Document exceeds 100-page limit");
            const candidate = regionAt(probe),
              top = Math.max(
                candidate.y,
                cursors.get(key(probe)) ?? candidate.y,
              );
            const candidateWidth =
              original.flowWidth === "node" || start === "body"
                ? Math.min(original.width, candidate.width)
                : candidate.width;
            const first = layoutText(
              remaining,
              candidateWidth,
              n.fontSize,
              n.lineHeight,
              n.fontFamily,
              n.fontWeight,
              n.align,
              options.typography(n),
              n.textHeightMode === "font-box",
            ).lines[0];
            const capacity = Math.min(
              candidate.y + candidate.height - top,
              n.maxHeight,
              n.flowGrow ? Infinity : requested,
            );
            if (
              (first.fontBoxBottom ?? first.bottom) <= capacity &&
              capacity >= n.minHeight
            )
              break;
            if (++attempts > regions.size * 2)
              throw new Error(
                `${n.name}: a text line cannot fit any flow region`,
              );
            probe = advance(probe, start);
          }
          position = probe;
          continue;
        }
        const last = fitting.at(-1)!,
          consumed = last.end;
        const chunk = sliceRuns(remaining, 0, consumed);
        const fragmentHeight = Math.min(
          room,
          Math.max(
            n.minHeight,
            last.fontBoxBottom ?? last.bottom,
            offset === 0 && consumed >= total ? Math.min(requested, room) : 0,
          ),
        );
        const end = offset + consumed;
        add(position, {
          ...n,
          id:
            offset === 0 && end === total ? n.id : `${n.id}_fragment_${offset}`,
          height: fragmentHeight,
          text: chunk.map((r) => r.text).join(""),
          richRuns: chunk,
          runs: text!.runs.filter((r) => r.line < fitting.length),
          clip: {
            x: region.x,
            y: cursor,
            width: n.width,
            height: fragmentHeight,
          },
          flowPlacement: { regionId: region.id, start: offset, end, total },
        });
        if (n.flowPush)
          cursors.set(
            key(position),
            cursor + fragmentHeight + (n.flowGap ?? region.gap),
          );
        offset = end;
        if (offset >= total) break;
        if (consumed <= 0)
          throw new Error(`${n.name}: text continuation made no progress`);
        position = advance(position, start);
        continue;
      }
      if (requested > room && cursor > region.y) {
        position = advance(position, start);
        continue;
      }
      // Keep non-text blocks intact where a later region can accommodate them.
      if (requested > room && region.next) {
        let next: string | null | undefined = region.next,
          candidatePosition: Position | undefined;
        while (next) {
          const candidate: Region = regions.get(next)!;
          const candidateTop =
            cursors.get(`${position.page}:${next}`) ?? candidate.y;
          if (requested <= candidate.y + candidate.height - candidateTop) {
            candidatePosition = { page: position.page, region: next };
            break;
          }
          next = candidate.next;
        }
        if (candidatePosition) {
          position = candidatePosition;
          continue;
        }
      }
      const boxHeight = Math.max(0, Math.min(requested, room));
      if (!boxHeight) {
        position = advance(position, start);
        continue;
      }
      const calendarScale = () =>
        calendarFitScale(
          calendarRuns(
            n.text,
            n.width,
            n.fontFamily,
            n.fontWeight,
            options.typography(n),
            n.fontSize,
            n.lineHeight,
            calendarAlignment(n),
          ),
          n.width,
          boxHeight,
          n.fontFamily,
          n.fontWeight,
          options.typography(n),
          calendarAlignment(n),
        );
      let clipped =
        measured > boxHeight ||
        (n.type === "calendar" && calendarScale() < 1 - 1e-7);
      if (text && (clipped || n.copyfit) && n.overflow === "shrink") {
        const fitted = options.fit
          ? options.fit(n, remaining, n.width, boxHeight)
          : fitText(
              remaining,
              n.width,
              boxHeight,
              n.fontSize,
              n.lineHeight,
              n.fontFamily,
              n.fontWeight,
              n.align,
              options.typography(n),
              n.allowGlyphOverhang,
              n.textHeightMode === "font-box",
              n.copyfit,
            );
        n.fontSize = fitted.fontSize;
        n.lineHeight = fitted.lineHeight;
        if (n.copyfit) n.richRuns = fitted.runs;
        text = fitted.layout;
        clipped =
          !fitted.fits ||
          textFrameHeight(text, false, n.textHeightMode === "font-box") >
            boxHeight;
      }
      if (n.type === "calendar" && clipped && n.overflow === "shrink") {
        n.fontSize = Math.max(
          Math.min(4, n.fontSize),
          n.fontSize * calendarScale(),
        );
        clipped = calendarScale() < 1 - 1e-7;
      }
      if (clipped) warn(n, pageAt(position.page));
      add(position, {
        ...n,
        height: boxHeight,
        runs: text?.runs,
        clip: { x: region.x, y: cursor, width: n.width, height: boxHeight },
        flowPlacement: { regionId: region.id, start: 0, end: total, total },
      });
      if (n.flowPush)
        cursors.set(
          key(position),
          cursor + boxHeight + (n.flowGap ?? region.gap),
        );
      break;
    }
    threads.set(start, position);
  }
  return pages;
}
